import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { CSSProperties, useEffect, useState, useRef } from 'react';
import { Form, FormControlProps, Overlay, Tooltip } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';

export interface SearchBoxProps extends FormControlProps {
  /** Key used in the query string (e.g. "search" or "status") */
  queryParamName?: string;
  /** Whether to preserve existing query params when updating */
  preserveQueryParams?: boolean;
  className?: string;
  inputClassName?: string;
  formClassName?: string;
  size?: 'sm' | 'lg';
  style?: CSSProperties;
  numericOnly?: boolean;
  validate?: (value: string) => boolean;
  tooltipMessage?: string;
  tooltipDuration?: number;
}

const SearchBox = ({
  placeholder = 'Search',
  size,
  className,
  inputClassName,
  formClassName,
  style,
  queryParamName = 'search',
  preserveQueryParams = false,
  defaultValue,
  numericOnly = false,
  validate,
  tooltipMessage,
  tooltipDuration = 2000,
  ...rest
}: SearchBoxProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);

  const initial = defaultValue?.toString() || new URLSearchParams(location.search).get(queryParamName) || '';

  const [searchTerm, setSearchTerm] = useState(initial);
  const [showTooltip, setShowTooltip] = useState(false);

  const validateInput = (value: string): boolean => {
    if (validate) {
      return validate(value);
    }
    if (numericOnly) {
      return value === '' || /^\d+$/.test(value);
    }
    return true;
  };

  const getTooltipMessage = (): string => {
    if (tooltipMessage) {
      return tooltipMessage;
    }
    if (numericOnly) {
      return 'Only numbers are allowed!';
    }
    return 'Invalid input';
  };

  // Sync from URL changes
  useEffect(() => {
    const urlVal = new URLSearchParams(location.search).get(queryParamName) || '';
    if (urlVal !== searchTerm) {
      setSearchTerm(urlVal);
    }
  }, [location.search, queryParamName]);

  // Sync when defaultValue prop changes (e.g., when applying saved filters)
  useEffect(() => {
    const propVal = defaultValue?.toString() || '';
    if (propVal !== searchTerm) {
      setSearchTerm(propVal);
    }
  }, [defaultValue]);

  useEffect(() => {
    if (showTooltip) {
      const timer = setTimeout(() => {
        setShowTooltip(false);
      }, tooltipDuration);
      return () => clearTimeout(timer);
    }
  }, [showTooltip, tooltipDuration]);

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = searchTerm.trim();

    const params = preserveQueryParams ? new URLSearchParams(location.search) : new URLSearchParams();

    if (trimmed) {
      params.set(queryParamName, trimmed);
    } else {
      params.delete(queryParamName);
    }

    const searchString = params.toString();
    const path = location.pathname + (searchString ? `?${searchString}` : '');
    navigate(path, { replace: true });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    if (!validateInput(newValue)) {
      setShowTooltip(true);
      return;
    }

    setSearchTerm(newValue);
  };

  return (
    <div className={classNames('search-box', className)} style={style}>
      <form className={classNames('position-relative', formClassName)} onSubmit={handleSearch}>
        <Form.Control
          ref={inputRef}
          type="search"
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleChange}
          className={classNames('search-input search', inputClassName)}
          size={size}
          inputMode={numericOnly ? 'numeric' : 'text'}
          pattern={numericOnly ? '[0-9]*' : undefined}
          {...rest}
        />
        <FontAwesomeIcon icon={faSearch} className="search-box-icon" />

        <Overlay target={inputRef.current} show={showTooltip} placement="top">
          {(props) => (
            <Tooltip id={`tooltip-${queryParamName}`} {...props}>
              {getTooltipMessage()}
            </Tooltip>
          )}
        </Overlay>
      </form>
    </div>
  );
};

export default SearchBox;
